/* eslint-disable */
import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import JoditEditor from "jodit-react";

/*------ import url and method ------*/
import * as url from "helper/UrlHelper";
import { getData, postData, putData, uploadSingleFile } from "utils/Gateway";

const SaveTaskPopup = ({
  setShowAlert,
  setAlertMessage,
  setMessageType,
  selectedTaskId,
  setSelectedTaskId,
  AfterPopupClose,
}) => {
  const { t } = useTranslation();
  const todayValue = new Date().toISOString().split("T")[0];
  const params = useParams();
  const token = localStorage.getItem("token");

  const [title, settitle] = useState("");
  const [details, setdetails] = useState("");
  const [hours, sethours] = useState("");
  const [score, setScore] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, seterrorMessage] = useState("");
  const [validation, setValidation] = useState({});

  const editor = useRef(null);
  const offcanvasBodyRef = useRef(null); // Ref for the scrollable area

  const editorCustomConfig = {
    readonly: false,
    placeholder: "",
    minHeight: 300,
    uploader: { insertImageAsBase64URI: false },
    events: {
      afterPaste: async (event) => {
        const clipboardData = event.clipboardData || window.clipboardData;
        const items = clipboardData.items;
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.indexOf("image") !== -1) {
            const file = items[i].getAsFile();
            const timestamp = Date.now();
            const fileExtension = file.type.split("/")[1];
            const newFileName = `image-${timestamp}.${fileExtension}`;
            const renamedFile = new File([file], newFileName, {
              type: file.type,
            });
            try {
              const requestUrl =
                url.API_BASE_URL +
                url.API_SINGLE_FILE_UPLOAD +
                ``;
              const response = await uploadSingleFile(requestUrl, renamedFile);
              if (response.status) {
                const imageUrl = url.SERVER_URL + response.data.path;
                const editorInstance = editor.current;
                if (editorInstance) {
                  const content = editorInstance.value;
                  const regex = /<img[^>]+src="data:image\/[^">]+"/g;
                  const newContent = content.replace(regex, (match) => {
                    const base64DataMatch = match.match(/src="([^"]+)"/);
                    if (
                      base64DataMatch &&
                      base64DataMatch[1].startsWith("data:image/")
                    ) {
                      return match.replace(base64DataMatch[1], imageUrl);
                    }
                    return match;
                  });
                  editorInstance.value = newContent;
                  setdetails(newContent);
                }
              }
            } catch (error) {
              console.error("Image upload failed:", error);
            }
          }
        }
      },
    },
    defaultActionOnPaste: "insert_as_html",
    defaultLineHeight: 1.5,
    enter: "div",
    statusbar: false,
    toolbarAdaptive: false,
    disablePlugins: [
      "image",
      "video",
      "about",
      "classSpan",
      "file",
      "speechRecognize",
      "copyformat",
      "spellcheck",
      "add-new-line",
    ],
  };

  const editorConfig = useMemo(() => editorCustomConfig, []);

  const validationHandler = () => {
    let isValid = true;
    const newValidation = {};
    if (!title || title.trim() === "") {
      newValidation.nameWarning = t("Please enter a name for this task.");
      isValid = false;
    }
    if (!details || details.trim() === "") {
      newValidation.detailsWarning = t(
        "Please provide instructions or details for this task."
      );
      isValid = false;
    }
    if (!hours || isNaN(hours) || Number(hours) <= 0) {
      newValidation.hoursWarning = t(
        "Please enter a valid duration in minutes (must be a number greater than 0)."
      );
      isValid = false;
    }
    if (!score || isNaN(score) || Number(score) < 0) {
      newValidation.scoreWarning = t(
        "Please enter a valid score (must be a number, 0 or greater)."
      );
      isValid = false;
    }
    setValidation(newValidation);
    return isValid;
  };

  const getTaskDetails = async () => {
    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_TASK_DETAILS +
        `/${selectedTaskId}`;
      const response = await getData(requestUrl);
      if (response.status) {
        settitle(response.data.title);
        setdetails(response.data.description);
        sethours(response.data.hours ?? 0);
        setScore(response.data.score ?? 0);
      } else {
        seterrorMessage(response.message);
        setMessageType("error");
        setAlertMessage(response.message);
        setShowAlert(true);
      }
    } catch (error) {
      setMessageType("error");
      setAlertMessage(error.message);
      setShowAlert(true);
    }
  };

  const saveTaskHandler = async () => {
    if (validationHandler()) {
      seterrorMessage("");
      try {
        setIsSaving(true);
        let taskData = {
          lessonid: params.lessonid,
          course: params.id,
          title,
          description: details,
          startdate: todayValue,
          enddate: todayValue,
          hours,
          score,
          leadid: null,
          project: null,
          contact: null,
          ismilestone: false,
          projectsection: null,
          previoustask: null,
          priority: "2",
          completedpercent: "0",
          taskpercent: 0,
          assigneduser: null,
          followers: [],
          rank: 1,
          uploads: [],
        };

        let response = {};
        if (selectedTaskId) {
          let requestUrl =
            url.API_BASE_URL +
            url.API_UPDATE_PROJECT_TASK +
            `/${selectedTaskId}`;
          response = await putData(requestUrl, taskData);
        } else {
          let requestUrl =
            url.API_BASE_URL + url.API_ADD_NEW_PROJECT_TASK;
          response = await postData(requestUrl, taskData);
        }

        setIsSaving(false);

        if (response.status) {
          resetHandler();
          AfterPopupClose();
          setMessageType("success");
          let offCanvasPopup = document.querySelector("#saveTask");
          let offCanvas = bootstrap.Offcanvas.getInstance(offCanvasPopup);
          offCanvas.hide();
        } else {
          seterrorMessage(response.message);
          setMessageType("error");
        }
        setAlertMessage(response.message);
        setShowAlert(true);
      } catch (error) {
        setMessageType("error");
        setAlertMessage(error.message);
        setShowAlert(true);
      }
    } else {
      seterrorMessage(
        t("Please correct the errors highlighted above before saving.")
      );
      // Scroll to the top of the offcanvas body
      if (offcanvasBodyRef.current) {
        offcanvasBodyRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }
  };

  const resetHandler = () => {
    settitle("");
    setdetails("");
    sethours("");
    setScore("");
    setSelectedTaskId(null);
    seterrorMessage("");
    setValidation({});
  };

  useEffect(() => {
    if (selectedTaskId) {
      getTaskDetails();
    }
  }, [selectedTaskId]);

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="saveTask"
      aria-labelledby="saveTaskLabel"
    >
      <div className="offcanvas-header p-4 pb-3 border-bottom border-gray-300 bg-primary-subtle">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-primary rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-white fs-5">
              task
            </span>{" "}
            {/* Using 'task' icon */}
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-primary mb-0"
              id="offcanvasLabelDetailsLabel"
            >
              {selectedTaskId ? t("Edit Task") : t("Add New Task")}
            </h3>
            <p className="text-muted fs-sm mb-0">
              {selectedTaskId
                ? t("Manage existing task details")
                : t("Define a new task")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-2 bg-white rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>
      <div
        className="offcanvas-body p-4 d-flex flex-column"
        ref={offcanvasBodyRef}
      >
        <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
          <form
            onSubmit={(e) => e.preventDefault()}
            className="d-flex flex-column h-100"
          >
            <div className="fields-container flex-fill">
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="taskName"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Task Name")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="taskName"
                  className={`form-control fs-sm shadow-none ${
                    validation.nameWarning
                      ? "is-invalid"
                      : "border border-gray-300"
                  }`}
                  placeholder={t("e.g., Complete Chapter 1 Quiz")}
                  value={title}
                  onChange={(e) => {
                    settitle(e.target.value);
                    setValidation((prev) => ({ ...prev, nameWarning: false }));
                    seterrorMessage("");
                  }}
                />
                {validation.nameWarning && (
                  <div className="invalid-feedback d-block">
                    {validation.nameWarning}
                  </div>
                )}
              </div>

              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="details"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Details")} <span className="text-danger">*</span>
                </label>
                <JoditEditor
                  ref={editor}
                  value={details}
                  config={editorConfig}
                  onBlur={(newContent) => {
                    setdetails(newContent);
                    setValidation((prev) => ({
                      ...prev,
                      detailsWarning: "",
                    }));
                    seterrorMessage("");
                  }}
                />
                {validation.detailsWarning && (
                  <div className="invalid-feedback d-block">
                    {validation.detailsWarning}
                  </div>
                )}
              </div>

              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <div className="row">
                  <div className="col-md-6 mb-4 mb-md-0">
                    <label
                      htmlFor="time"
                      className="d-block fs-sm fw-semibold mb-2 text-dark"
                    >
                      {t("Duration (Minutes)")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      id="time"
                      min="1"
                      className={`form-control fs-sm shadow-none ${
                        validation.hoursWarning
                          ? "is-invalid"
                          : "border border-gray-300"
                      }`}
                      placeholder={t("e.g., 60")}
                      value={hours}
                      onChange={(e) => {
                        sethours(e.target.value);
                        setValidation((prev) => ({
                          ...prev,
                          hoursWarning: false,
                        }));
                        seterrorMessage("");
                      }}
                    />
                    {validation.hoursWarning && (
                      <div className="invalid-feedback d-block">
                        {validation.hoursWarning}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label
                      htmlFor="price"
                      className="d-block fs-sm fw-semibold mb-2 text-dark"
                    >
                      {t("Score")} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      id="price"
                      className={`form-control fs-sm shadow-none ${
                        validation.scoreWarning
                          ? "is-invalid"
                          : "border border-gray-300"
                      }`}
                      placeholder={t("e.g., 100")}
                      value={score}
                      onChange={(e) => {
                        setScore(e.target.value);
                        setValidation((prev) => ({
                          ...prev,
                          scoreWarning: false,
                        }));
                        seterrorMessage("");
                      }}
                    />
                    {validation.scoreWarning && (
                      <div className="invalid-feedback d-block">
                        {validation.scoreWarning}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="action d-flex justify-content-between gap-3 pt-3 border-top mt-auto">
              <Link
                to="#"
                className="btn btn-outline-primary d-flex align-items-center gap-2"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
                onClick={resetHandler}
              >
                <span className="material-symbols-outlined me-1">cancel</span>{" "}
                {t("Close")}
              </Link>
              <button
                onClick={saveTaskHandler}
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                disabled={isSaving}
                style={{
                  cursor: isSaving ? "not-allowed" : "pointer",
                }}
              >
                {isSaving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    {t("Saving...")}
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined me-1">save</span>{" "}
                    {t("Save Task")}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SaveTaskPopup;
